export const CUSTOMER_HEALTH_STATUS_COLORS = {
  Healthy: "#73AA24",
  Warning: "#D0B232",
  Critical: "#D77440",
  NA: "#888888"
} as const;

export const CUSTOMER_HEALTH_PIVOT_TABS = [
  { headerText: "Overview", itemKey: "overview" },
  { headerText: "MTP", itemKey: "mtp" },
  { headerText: "Purview", itemKey: "purview" }
] as const;

export const CUSTOMER_HEALTH_TABS_DATA = {
  pivot: { ariaLabel: "customer-health-pivot-selection" },
  pivotItems: CUSTOMER_HEALTH_PIVOT_TABS
} as const;

export type CustomerHealthTabKey = (typeof CUSTOMER_HEALTH_PIVOT_TABS)[number]["itemKey"];

export const DEFAULT_CUSTOMER_HEALTH_TAB: CustomerHealthTabKey = "mtp";

export const CUSTOMER_HEALTH_TAB_CONTENT: Record<CustomerHealthTabKey, { placeholderText: string }> = {
  overview: { placeholderText: "Overview Tab" },
  mtp: { placeholderText: "MTP Tab" },
  purview: { placeholderText: "Purview Tab" }
};

export const MTPCustomerHealthTooltips = {
  ScoreGauge:
    "Overall health of the customer's Microsoft Threat Protection products, with the trend versus last month.",
  HeatMap:
    "At-a-glance health of each product across key indicators, color-coded for quick review.",
  Indicators:
    "Key factors that make up a product's health score; select one to explore.",
  RawData:
    "Underlying data behind the selected indicator's score.",
  Products: {
    "Microsoft Defender for Cloud Apps": "Health of the customer's Defender for Cloud Apps adoption.",
    "Microsoft Defender for Cloud": "Health of the customer's Defender for Cloud adoption.",
    "Microsoft Defender for Endpoint": "Health of the customer's Defender for Endpoint adoption.",
    "Microsoft Defender for Identity": "Health of the customer's Defender for Identity adoption.",
    "Microsoft Defender for Office 365": "Health of the customer's Defender for Office 365 adoption.",
    "Microsoft Sentinel": "Health of the customer's Sentinel adoption."
  } as Record<string, string>,
  IndicatorDescriptions: (() => {
    const deployment =
      "How widely the customer has rolled out what they've purchased or consumed. Assess the extent to which the customer has implemented or utilized their licensed or consumption-based security products.";
    const productEngagement =
      "Measures how actively and effectively the customer is using the products (e.g., frequency and depth of usage).";
    const supportHealth =
      "How healthy the customer's overall support experience is. The SHI value is marked as NA (Not Available) when there were no open support cases or relevant KPIs for the selected product and tenant during the last monthly scoring snapshot. This means a score could not be calculated for that period. Please note that the SHI score is based on an end-of-month snapshot, whereas the Raw Data view shows the current (latest) snapshot of support cases. As a result, the SHI score and the raw data may not always match since they represent different points in time.";
    const communityEngagement =
      "How engaged the customer is across community and feedback channels. Assess the customer's participation in customer feedback programs (surveys, previews, user group etc) as a proxy for advocacy and collaborative engagement.";
    const churn = "Whether the customer's usage is growing or declining.";
    const maturity =
      "How well the customer operates and gets value from the product, beyond basic setup. Measures how well a product or workload is configured and used consistently according to best practices, covering features, policies, and current security standards.";
    const featureHealth =
      "Captures un-resolved high-impact feature requests or product design changes health score which is a crucial factor to assess customer satisfaction with security products.";
    return {
      Deployment: deployment,
      ProductEngagement: productEngagement,
      Engagement: productEngagement,
      SHIScore: supportHealth,
      SupportHealthScore: supportHealth,
      CommunityEngagement: communityEngagement,
      Churn: churn,
      ChurnRate4Months: churn,
      MaturityHealthScore: maturity,
      FeatureHealthScore: featureHealth,
      FeatureRequestHealth: featureHealth
    } as Record<string, string>;
  })()
};

export const PurviewCustomerHealthTooltips = {
  ProductsHeader:
    "Health scores for each Purview product (8 products, equal weight); select a product card to view its indicators and raw data. Trend shown vs. previous month.",
  Indicators:
    "Key factors that make up a product's health score (weighted contribution); select one to explore raw data and methodology.",
  RawData:
    "Underlying data behind the selected indicator's score.",
  Products: {
    "Microsoft Purview Audit": "Health of the customer's Purview Audit adoption.",
    "Microsoft Purview Communication Compliance": "Health of the customer's Purview Communication Compliance adoption.",
    "Microsoft Purview Compliance Manager": "Health of the customer's Purview Compliance Manager adoption.",
    "Microsoft Purview Data Lifecycle Management": "Health of the customer's Purview Data Lifecycle Management adoption.",
    "Microsoft Purview Data Loss Prevention": "Health of the customer's Purview Data Loss Prevention adoption.",
    "Microsoft Purview Insider Risk Management": "Health of the customer's Purview Insider Risk Management adoption.",
    "Microsoft Purview Information Protection": "Health of the customer's Purview Information Protection adoption.",
    "Microsoft Purview eDiscovery": "Health of the customer's Purview eDiscovery adoption."
  } as Record<string, string>,
  IndicatorDescriptions: (() => {
    const deployment =
      "Measures deployment penetration for each Purview feature within a tenant by comparing Monthly Protected Users (MPU) against Paid Available Units (PAU).";
    const engagement =
      "Measures product engagement depth for each Purview feature by comparing Monthly Deep Protected Usage (MDPU) against Monthly Protected Users (MPU). Since there is no direct engagement metric, MDPU serves as a proxy - it captures how many deep/advanced features of a product the customer is adopting, using, or engaged with.";
    const communityEngagement =
      "How engaged the customer is across community and feedback channels. Assess the customer's participation in customer feedback programs (surveys, previews, user group etc) as a proxy for advocacy and collaborative engagement.";
    const churn =
      "Measures retention health - the inverse of churn risk. A higher score means the tenant is healthier (less likely to churn). Evaluates three behavioral signals - 3-month MPU stability, DPU (deep usage) stability, and current DPU utilization - to produce a composite churn health score for a Purview product.";
    const featureRequest =
      "Captures un-resolved high-impact feature requests or product design changes health score which is a crucial factor to assess customer satisfaction with purview products.";
    const supportHealth =
      "How healthy the customer's overall support experience is. Marked NA when there were no open support cases or relevant KPIs for the product and tenant in the last monthly snapshot. SHI uses an end-of-month snapshot while Raw Data shows the current snapshot, so they may not always match.";
    return {
      Deployment: deployment,
      Engagement: engagement,
      ProductEngagement: engagement,
      CommunityEngagement: communityEngagement,
      Churn: churn,
      ChurnRate4Months: churn,
      FeatureRequestHealth: featureRequest,
      FeatureHealthScore: featureRequest,
      SHIScore: supportHealth,
      SupportHealthScore: supportHealth
    } as Record<string, string>;
  })()
};

export const IntuneCustomerHealthTooltips = {
  ScoreGauge:
    "Overall health of the customer's Microsoft Intune adoption, with the trend versus last month.",
  HeatMap:
    "At-a-glance health of Microsoft Intune across key indicators, color-coded for quick review.",
  ProductsHeader:
    "Health score for the Intune product; select the product card to view indicators and raw data. Trend shown vs. previous month.",
  Indicators:
    "Key factors that make up the product's health score (weighted contribution); select one to explore raw data and methodology.",
  RawData:
    "Indicator-level details and factors behind the selected indicator's score.",
  Products: {
    "Microsoft Intune": "Overall health of the customer's Microsoft Intune adoption."
  } as Record<string, string>,
  IndicatorDescriptions: (() => {
    const deployment =
      "Composite of three sub-indicators: how much of purchased licenses are actively used, how recently managed devices checked in, and the diversity of OS platforms enrolled in Intune.";
    const supportHealth =
      "How healthy the customer's overall support experience is. Marked NA when there were no open support cases or relevant KPIs for the tenant in the last monthly snapshot. SHI uses an end-of-month snapshot while Raw Data shows the current snapshot, so they may not always match.";
    const featureHealth =
      "Captures un-resolved high-impact feature requests or product design changes health score which is a crucial factor to assess customer satisfaction with security products.";
    const catSentiment =
      "Measures whether the tenant has an active customer engagement for Intune. Checks whether a 'CxP CAT Lead' contact in CRM is actively associated with the tenant's Intune project with no end date; if present and not disengaged, the tenant has active CVC (CAT) support.";
    const usageTrajectory =
      "Measures whether usage is growing, stable, or declining over time. Combines three equally-weighted signals - MAU growth vs. 4-month average, platform breadth, and management-scope expansion (MDM/MAM adoption + Intune Suite purchase) - vs. a 4-month rolling average to reward growth and penalize decline. Here MAU refers to Monthly Active Users, MDM refers to Mobile Device Management and MAM refers to Mobile Application management.";
    return {
      Deployment: deployment,
      SHIScore: supportHealth,
      SupportHealthScore: supportHealth,
      FeatureHealthScore: featureHealth,
      FeatureRequestHealth: featureHealth,
      CATSentimentScore: catSentiment,
      CatSentimentScore: catSentiment,
      UsageTrajectoryScore: usageTrajectory
    } as Record<string, string>;
  })()
};

const normalizeIndicatorKey = (key: string): string =>
  key.toLowerCase().replace(/[^a-z0-9]/g, "");

export const resolveIndicatorTooltip = (
  rawKey: string | undefined | null,
  tooltipMap: Record<string, string>
): string | undefined => {
  if (!rawKey) return undefined;
  if (tooltipMap[rawKey]) return tooltipMap[rawKey];
  const target = normalizeIndicatorKey(rawKey);
  if (!target) return undefined;
  for (const [key, tooltip] of Object.entries(tooltipMap)) {
    if (normalizeIndicatorKey(key) === target) return tooltip;
  }
  return undefined;
};

export const getIndicatorTooltipMap = (
  productFamily: string
): Record<string, string> => {
  switch (productFamily) {
    case "MTP":
      return MTPCustomerHealthTooltips.IndicatorDescriptions;
    case "Purview":
      return PurviewCustomerHealthTooltips.IndicatorDescriptions;
    case "Intune":
      return IntuneCustomerHealthTooltips.IndicatorDescriptions;
    default:
      return {};
  }
};
